import {
  OrderDestinationStatus,
  OrderOverallStatus,
  OrderDriverStatus,
  OrderEventTypes,
  OrderActionTypes,
  OrderStatus,
  OrderImageTypes,
  Order,
  OrderPODStatus,
  AdminPermission,
} from '@hero/schema';

/**
 * what is the activity based on a given status.
 * - `active` = means the order is still active
 * - `inactive` = means the order is inactive maybe because the order status is delivered / or canceled
 * - `driver_assigned` = this is for driver status which means the order has been assigned to a driver.
 * - `not_assigned` = this is for driver status which means the order has NOT been assigned a driver yet.
 */
//activity: 'active' | 'in_active' | 'driver_assigned' | 'driver_not_assigned';
export type ActionConfig<T> =
  | Readonly<{
      onPickup: T | undefined;
      onDropOff: T | undefined;
    }>
  | undefined
  | T;

type ActionAndStatusConfig = {
  [key in OrderActionTypes]: Readonly<{
    /**
     * the event to produce in the timeline given an action.
     * for example, when the new new action is `confirm_order`, then an event called `order_is_confirmed` must be produced.
     *
     * if `undefined`, then it means that the action does not produce any event.
     *
     * If multi options, then it means the action produces multiple events, and it will be created **based on order in the array**.
     */
    events: ActionConfig<OrderEventTypes[]>;
    /** same as `events`, but only for the first destination start. For example, if the action is `in_transit` and that happens on the first destination, then we will trigger `journey_start` event. */
    eventsOnFirstDestinationStart?: ActionConfig<OrderEventTypes[]>;
    otherActions?: {
      onLastDestination?: OrderActionTypes[];
      onNextDestination?: OrderActionTypes[];
    };
    /** the new driver status that should be produced based on the action.
     *
     * If `undefined`, then it means the action does not effect the driver status
     */
    driverStatus: ActionConfig<OrderStatus['driver']>;
    /** the new order status that should be produced based on the action.
     *
     * If `undefined`, then it means the action does not effect the order status
     *
     * If multi options, then it means that one of the status can be changed based on its logic such as `arrived_at_pickup`, and `arrived_at_drop` off
     */
    orderStatus: ActionConfig<OrderStatus['order']>;
    /** the new destination status that should be produced based on the action.
     * this is used for the overall destination
     * If `undefined`, then it means the action does not effect the destination status
     */
    destinationStatus: ActionConfig<OrderStatus['destination']>;
    /**
     * the active destination is the current destination that the driver is heading to either pickup / dropoff
     */
    activeDestinationStatus: ActionConfig<OrderStatus['destination']>;
    /** the next destination status */
    nextDestinationStatus: ActionConfig<OrderStatus['destination']>;
  }>;
};
const destinationStatusConfig = {
  active: ['new', 'arrived', 'in_transit'] as OrderDestinationStatus[],
  inactive: ['canceled', 'done', 'upcoming'] as OrderDestinationStatus[],
} as const;

const orderStatusConfig = {
  active: [
    'new',
    'confirmed',
    'arrived_dropoff',
    'arrived_pickup',
    'in_transit_dropoff',
    'in_transit_pickup',
  ] as OrderOverallStatus[],
  inactive: ['delivered', 'canceled', 'closed'] as OrderOverallStatus[],
  /**
   * the order status that will be overridden when the driver is assigned.
   * this is used to override the order status when the driver is assigned to a driver.
   * for example, if the order status is `accepted`, then the order status will be overridden to `confirmed`.
   * this is used to avoid the order status from being overridden to `delivered` when the driver is assigned to a driver.
   */
  autoConfirmOrderStatus: ['new', 'accepted'] as OrderOverallStatus[],
} as const;

const orderDriverStatusConfig = {
  notAssigned: ['not_assigned', 'accepted', 'searching_for_driver'] as OrderDriverStatus[],
  assigned: ['assigned'] as OrderDriverStatus[],
} as const;

// * config
export const statusActivityConfig = {
  destination: destinationStatusConfig,
  order: orderStatusConfig,
  driver: orderDriverStatusConfig,
} as const;

export const actionConfig: ActionAndStatusConfig = {
  accept_order: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    orderStatus: 'accepted',
    driverStatus: undefined,
    events: ['order_accepted'],
  },
  confirm_order: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    orderStatus: 'confirmed',
    driverStatus: undefined,
    events: ['order_is_confirmed'],
  },

  book_order: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: undefined, // this will be determined in code order-action.service.ts (bookingOrderStatus())
    events: ['order_booked'],
    orderStatus: undefined,
  },

  look_for_driver: {
    orderStatus: undefined,
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: 'searching_for_driver',
    events: ['searching_for_driver'],
  },

  accept_and_publish_order: {
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    destinationStatus: undefined,
    driverStatus: 'searching_for_driver',
    events: ['order_accepted', 'searching_for_driver'],
    orderStatus: 'accepted',
  },

  publish_order: {
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    destinationStatus: undefined,
    driverStatus: 'searching_for_driver',
    events: ['searching_for_driver'],
    orderStatus: undefined,
  },
  unable_to_find_carrier: {
    orderStatus: 'canceled',
    driverStatus: 'unable_to_find_carrier',
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    events: ['unable_to_find_carrier'],
  },

  ready_to_start_journey: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: undefined,
    events: ['ready_to_start_journey'],
    orderStatus: 'confirmed',
  },

  journey_start: {
    destinationStatus: 'in_transit',
    activeDestinationStatus: 'in_transit',
    nextDestinationStatus: 'upcoming',
    orderStatus: 'in_transit_pickup',
    driverStatus: undefined,
    events: ['journey_start', 'in_transit'], // it will create 2 events (1 - journey_start, 2 - in_transit)
  },

  in_transit: {
    destinationStatus: 'in_transit',
    activeDestinationStatus: 'in_transit',
    nextDestinationStatus: 'upcoming',
    driverStatus: undefined,
    // the journey_start start event will be triggered on when the status of the first destination changes to `in_transit`, and only `journey_start` event has not been triggered before.
    eventsOnFirstDestinationStart: ['journey_start', 'in_transit'],
    events: ['in_transit'],
    orderStatus: {
      onPickup: 'in_transit_pickup',
      onDropOff: 'in_transit_dropoff',
    },
  },

  arrived: {
    events: ['arrived'],
    destinationStatus: 'arrived',
    activeDestinationStatus: 'arrived',
    nextDestinationStatus: undefined,
    driverStatus: undefined,
    orderStatus: {
      onDropOff: 'arrived_dropoff',
      onPickup: 'arrived_pickup',
    },
  },
  items_done: {
    destinationStatus: undefined,
    activeDestinationStatus: 'done',
    nextDestinationStatus: 'new',
    otherActions: {
      onLastDestination: ['delivered'], // if this the last destination, then trigger the delivered action
      onNextDestination: ['in_transit'], // if there is a next destination to go to, then trigger the the in_transit action
    },
    events: {
      /**
       *
       * in some cases, the items will be loaded / unloaded but still the driver has to wait for some papers ...
       * for now we're just producing the destination_done assuming that the driver will head to the next destination
       * as soon as he pickup or dropoff items.
       *
       * So later on we can break the destination_done to a diff action when the driver is really done with the destination
       * */
      onDropOff: ['items_dropped_off', 'destination_done'],
      onPickup: ['items_picked_up', 'destination_done'],
    },
    driverStatus: undefined,
    orderStatus: undefined,
  },

  rest_start: {
    // @TBC -- I think we need to add rest as status in the destination
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: undefined,
    events: ['driver_rest_start'],
    orderStatus: undefined,
  },
  rest_end: {
    // @TBC -- I think we need to add rest as status in the destination
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: undefined,
    events: ['driver_rest_end'],
    orderStatus: undefined,
  },
  delivered: {
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    destinationStatus: 'done',
    driverStatus: undefined,
    orderStatus: 'delivered',
    events: ['delivered'],
  },

  cancel_order: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    orderStatus: 'canceled',
    driverStatus: undefined,
    events: ['customer_canceled_order'],
  },
  close_order: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    orderStatus: 'closed',
    driverStatus: undefined,
    events: ['order_closed'],
  },
  super_admin_canceled_order: {
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: undefined,
    events: ['super_admin_canceled_order'],
    orderStatus: 'canceled',
  },
  return_order: {
    // @TBC --- we need to add return order flow
    destinationStatus: undefined,
    activeDestinationStatus: undefined,
    nextDestinationStatus: undefined,
    driverStatus: undefined,
    events: undefined,
    orderStatus: undefined,
  },
} as const;

export type OrderNextStepConfig = {
  [key in OrderOverallStatus]:
    | {
        nextAction: OrderActionTypes;
        image?: OrderImageTypes;
        isLastStep?: boolean;
        /** this should display a text field for the driver to enter a reference number for
         * PDF such as `IN12121`
         */
        enterRefNumber?: boolean;
      }
    | undefined;
};
export const carrierNextStepConfig: OrderNextStepConfig = {
  // #1
  new: {
    nextAction: 'journey_start',
  },

  // #1
  confirmed: {
    nextAction: 'journey_start',
  },

  // #2
  in_transit_pickup: {
    nextAction: 'arrived',
    image: 'place', // make the driver take a picture of the place
  },

  // #3
  arrived_pickup: {
    nextAction: 'items_done',
    image: 'proof_of_pickup_doc', // proof of pickup for the
    enterRefNumber: true, // the driver must enter a ref number
  },

  // #4
  in_transit_dropoff: {
    nextAction: 'arrived',
    image: 'place',
  },

  // #5
  arrived_dropoff: {
    nextAction: 'items_done',
    image: 'proof_of_delivery_doc',
    enterRefNumber: true, // the driver must enter a ref number
    isLastStep: true,
  },
  accepted: undefined,
  canceled: undefined,
  delivered: undefined,
  closed: undefined,
};

export type AdminNextStepConfig = {
  [key in OrderOverallStatus]:
    | {
        /** possible next actions */
        nextActions: OrderActionTypes[];
        isLastStep?: boolean;
        /** is cancelable at this step or not. If empty it means not cancelable */
        isCancelable?: 'yes_without_charges' | 'yes_charges_might_apply';
      }
    | undefined;
};

export const adminNextStepConfig: AdminNextStepConfig = {
  new: {
    nextActions: ['accept_and_publish_order', 'accept_order'],
    isCancelable: 'yes_without_charges',
  },

  accepted: {
    nextActions: ['publish_order'],
    isCancelable: 'yes_without_charges',
  },

  confirmed: {
    nextActions: ['journey_start'],
    isCancelable: 'yes_without_charges',
  },

  in_transit_pickup: {
    nextActions: ['arrived'],
    isCancelable: 'yes_without_charges',
  },

  arrived_pickup: {
    nextActions: ['items_done'],
    isCancelable: 'yes_without_charges',
  },

  in_transit_dropoff: {
    nextActions: ['arrived'],
    isCancelable: 'yes_without_charges',
  },
  arrived_dropoff: {
    nextActions: ['items_done'],
    isCancelable: 'yes_without_charges',
  },

  canceled: undefined, // means nothing next
  delivered: undefined, // means nothing next
  closed: undefined,
};

// * fields
type OrderFields =
  | 'customer_price'
  | 'carrier_price'
  | 'carrier'
  | 'driver'
  | 'vehicle'
  | 'destination'
  | 'pod_images'
  | 'pod_status'
  | 'order_dates';

type OrderFieldsConfig = Readonly<
  Record<
    OrderFields,
    {
      disableEditsInStatus: Readonly<(OrderOverallStatus | 'always_allowed')[]>;
      /** if provided, then the field will be enabled for the given user roles. */
      alwaysEnableForUserRole?: AdminPermission[];
    }
  >
>;
const completedStatuses: OrderOverallStatus[] = ['delivered', 'canceled', 'closed'] as const;
export const orderFieldsConfig: OrderFieldsConfig = {
  customer_price: {
    disableEditsInStatus: completedStatuses,
    alwaysEnableForUserRole: ['system_admin'],
  },
  carrier_price: {
    disableEditsInStatus: completedStatuses,
    alwaysEnableForUserRole: ['system_admin'],
  },
  carrier: {
    disableEditsInStatus: completedStatuses,
  },
  driver: {
    disableEditsInStatus: completedStatuses,
  },
  vehicle: {
    disableEditsInStatus: completedStatuses,
  },
  destination: {
    disableEditsInStatus: completedStatuses,
  },
  order_dates: {
    disableEditsInStatus: completedStatuses,
  },
  pod_images: {
    disableEditsInStatus: ['always_allowed'],
  },
  pod_status: {
    disableEditsInStatus: ['always_allowed'],
  },
} as const;

type PODStatusConfig = Readonly<
  Record<
    OrderPODStatus,
    {
      nextStatus: OrderPODStatus | undefined;
      /** if true, then we must assign an admin user so that who is responsible for the POD  at this stage */
      mustAssignOwner: boolean;
      events: OrderEventTypes[];
      showConfirmationDialog: boolean;
    }
  >
>;
export const podStatusConfig: PODStatusConfig = {
  // #0 initial status - POD not required
  no_pod_required: {
    nextStatus: 'not_received',
    mustAssignOwner: false,
    events: [],
    showConfirmationDialog: false,
  },

  // #1 with the carrier not received yet.
  not_received: {
    nextStatus: 'received',
    mustAssignOwner: true,

    // no events for not_received status because it is the initial status.
    // so this will be assumed to start when the order status is delivered
    events: [],
    showConfirmationDialog: false,
  },

  // #2 received at the office
  received: {
    nextStatus: 'under_review',
    mustAssignOwner: true,
    events: ['pod_received'],
    showConfirmationDialog: true,
  },

  // #3 reviewing PODs
  under_review: {
    nextStatus: 'ready_to_be_sent',
    mustAssignOwner: true,
    events: ['pod_under_review'],
    showConfirmationDialog: false,
  },

  // #4 ready to be sent to the customer
  ready_to_be_sent: {
    nextStatus: 'sent',
    mustAssignOwner: true,
    showConfirmationDialog: true,
    events: ['pod_ready_to_be_sent'],
  },

  // #5 sent to the customer
  sent: {
    nextStatus: 'delivered',
    mustAssignOwner: true,
    showConfirmationDialog: true,
    events: ['pod_sent'],
  },

  // #6 delivered to the customer
  delivered: {
    nextStatus: undefined,
    mustAssignOwner: false,
    showConfirmationDialog: true,
    events: ['pod_delivered'],
  },
};
